import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchWorksheets } from "../worksheetListSlice";
import { retrieveWorksheet } from "../worksheetSlice";
import { fetchUserInfo } from "../../auth/authSlice";
import { fetchLevels, fetchStudents } from "../../student/studentSlice";

import { formatDate } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import WorksheetModal from "./WorksheetModal/WorksheetModal";
import Pagination from "../../../components/Pagination";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetTypeBox from "./common/WorksheetTypeBox";

const WorksheetTable = () => {
  const dispatch = useDispatch();

  const { results, current_page, total_pages } = useSelector(
    (state) => state.worksheets
  );
  const { allStudents } = useSelector((state) => state.student);
  const { user } = useSelector((state) => state.auth);

  const isBasicPlan = user.plan === "basic";

  const [keyword, setKeyword] = useState("");
  const [typeSelect, setTypeSelect] = useState("all");
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page: 1, type: typeParam, keyword: keyword }));
    if (allStudents.length == 0) {
      dispatch(fetchStudents());
      dispatch(fetchLevels());
      dispatch(fetchUserInfo());
    }
  }, [dispatch, typeSelect, keyword]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handleWorksheetClick = async (id, type, author_name) => {
    await dispatch(retrieveWorksheet({ id, type, author_name }));
    setAssignmentModalOpen(true);
  };

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page, type: typeParam, keyword: keyword }));
  };

  const handlePreviewClick = (id, type) => {
    window.open(`https://www.ginedu.com/${type}/${id}/`, "_blank");
  };

  return (
    <div
      style={{
        padding: "10px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* 제목과 드롭다운을 같은 Row에 배치 */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h3 style={{ marginRight: "auto" }}>문제지 전체 목록</h3>
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div style={{ width: "30%", marginLeft: "10px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFF",
          paddingBottom: "5px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "10px",
          }}
        >
          <colgroup>
            <col style={{ width: "10%" }} />
            <col />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1px solid #EBEBEB",
                fontSize: "16px",
                color: "#737373",
              }}
            >
              <td style={{ padding: "10px" }}>문항 번호</td>
              <td style={{ padding: "10px" }}>학습지명</td>
              <td style={{ padding: "10px" }}>생성일</td>
              <td style={{ padding: "10px" }}>출제자</td>
              <td style={{ padding: "10px", textAlign: "center" }}>미리보기</td>
              <td style={{ padding: "10px", textAlign: "center" }}>출제</td>
            </tr>
            {results &&
              results.map((item, index) => (
                <tr
                  key={item.id}
                  style={{
                    fontSize: "18px",
                  }}
                >
                  <td style={{ padding: "10px" }}>{item.id}</td>
                  <td
                    style={{
                      padding: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {item.title}
                    <WorksheetTypeBox type={item.type_name} />
                  </td>
                  <td style={{ padding: "10px" }}>
                    {formatDate(item.created_at)}
                  </td>
                  <td style={{ padding: "10px" }}>{item.user_name}</td>
                  <td style={{ position: "relative" }}>
                    <button
                      onClick={() =>
                        handlePreviewClick(item.id, item.type_name)
                      }
                      style={{
                        backgroundColor: "#F6F8FA",
                        border: "1px solid #EBEBEB",
                        borderRadius: "5px",
                        padding: "5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                      title="문서 열기"
                    >
                      <DescriptionIcon
                        style={{
                          fill: "#000000",
                          fontSize: "24px",
                          stroke: "#EBEBEB",
                        }}
                      />
                    </button>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      disabled={isBasicPlan}
                      style={{
                        backgroundColor: isBasicPlan ? "#CCCCCC" : "#2D2D2D",
                        color: isBasicPlan ? "#999999" : "#FFFFFF",
                        border: "1px solid #EBEBEB",
                        borderRadius: "5px",
                        padding: "5px 30px",
                        cursor: isBasicPlan ? "not-allowed" : "pointer",
                        fontSize: "16px",
                        opacity: isBasicPlan ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (isBasicPlan) {
                          alert("기본 플랜에서는 과제를 출제할 수 없습니다. Plus 플랜으로 업그레이드해주세요.");
                        } else {
                          handleWorksheetClick(
                            item.id,
                            item.type_name,
                            item.user_name
                          );
                        }
                      }}
                      title={isBasicPlan ? "기본 플랜에서는 과제 출제가 불가능합니다" : "과제 출제하기"}
                    >
                      출제하기
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* 페이지네이션 */}
        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />

        {assignmentModalOpen && (
          <WorksheetModal
            onClose={() => setAssignmentModalOpen(false)}
          ></WorksheetModal>
        )}
      </div>
    </div>
  );
};

export default WorksheetTable;
